import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  PlayIcon,
  DocumentTextIcon
} from '@heroicons/react/24/solid';
import {
  ScissorsIcon,
  SparklesIcon,
  FaceSmileIcon,
  HeartIcon
} from '@heroicons/react/24/outline'; // Using these as placeholders for categories
import carouselHair from '../assets/carousel-hair.png';
import carouselBarber from '../assets/carousel-barber.png';
import carouselMassage from '../assets/carousel-massage.png';
import serviceManicure from '../assets/service-manicure.png';
import serviceMakeup from '../assets/service-makeup.png';
import serviceSkincare from '../assets/service-skincare.png';

const categories = [
  { name: 'Cabelereiro', icon: 'scissors' },
  { name: 'Barbeiros', icon: 'face' },
  { name: 'Manicure', icon: 'sparkles' },
  { name: 'Cuidados com a pele', icon: 'heart' },
  { name: 'Sobrancelhas e cílios', icon: 'eye' },
  { name: 'Dia de SPA', icon: 'star' },
  { name: 'Maquiagem', icon: 'brush' },
  { name: 'Saúde e bem-estar', icon: 'plus' },
  { name: 'Massoterapeuta', icon: 'hand' },
];

const recommendedSpots = [
  { id: 1, name: 'Studio Glamour', image: carouselHair, type: 'Cabelereiro', rating: 4.9, reviews: 120 },
  { id: 2, name: 'Barbearia Viking', image: carouselBarber, type: 'Barbearia', rating: 4.8, reviews: 85 },
  { id: 3, name: 'Zen Spa', image: carouselMassage, type: 'Massagem', rating: 5.0, reviews: 200 },
  { id: 4, name: 'Nails & Co.', image: serviceManicure, type: 'Manicure', rating: 4.7, reviews: 60 },
  { id: 5, name: 'MakeUp Art', image: serviceMakeup, type: 'Maquiagem', rating: 4.9, reviews: 45 },
  { id: 6, name: 'Pele de Seda', image: serviceSkincare, type: 'Estética', rating: 4.8, reviews: 90 },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [carouselHair, carouselBarber, carouselMassage];

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">

      {/* Header Overlay */}
      <nav className="absolute top-0 w-full z-50 p-6 flex justify-between items-center text-white bg-gradient-to-b from-black/50 to-transparent">
        <div className="flex items-center gap-2">
          <Link to="/" className="text-2xl font-bold tracking-tight">
            HoraMarcada<span className="text-green-400">Pro</span>
          </Link>
        </div>

        <div className="flex items-center gap-4 text-sm font-medium">
          <Link to="/business" className="hidden border border-white/30 rounded-full px-4 py-2 hover:bg-white/10 transition sm:block">
            Listar sua empresa
          </Link>
          <Link to="/login" className="hover:text-green-400 transition">
            Entrar / Inscrever-se
          </Link>
        </div>
      </nav>

      {/* Hero Carousel */}
      <section className="relative h-[600px] bg-black overflow-hidden flex items-center justify-center">
        {/* Carousel Images */}
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-70' : 'opacity-0'}`}
          >
            <img src={slide} alt="Background" className="w-full h-full object-cover" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 text-center w-full max-w-4xl px-4 mt-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-md">
            Seja ousado
          </h1>
          <p className="text-white/90 text-lg md:text-xl mb-10 drop-shadow-sm font-medium">
            Descubra e reserve profissionais de beleza e bem-estar perto de você
          </p>

          {/* Search Bar */}
          <div className="bg-white rounded-full p-2 flex items-center shadow-2xl max-w-2xl mx-auto h-16">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 ml-4" />
            <input
              type="text"
              placeholder="Pesquise serviços ou empresas"
              className="flex-1 px-4 text-gray-700 outline-none text-lg h-full"
            />
            <div className="h-8 w-px bg-gray-200 mx-2 hidden sm:block"></div>
            <div className="hidden sm:flex items-center gap-2 px-4 text-gray-500 hover:text-gray-700 cursor-pointer">
              <MapPinIcon className="h-5 w-5" />
              <span>Sua localização</span>
            </div>
            <button className="bg-green-600 text-white rounded-full px-8 h-full font-bold hover:bg-green-700 transition hidden sm:block">
              Buscar
            </button>
          </div>
        </div>

        {/* Categories Bottom Bar */}
        <div className="absolute bottom-0 w-full bg-black/40 backdrop-blur-md py-4 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 flex justify-between gap-8 min-w-max">
            {categories.map((cat, idx) => (
              <Link key={idx} to="/explorar" className="flex flex-col items-center gap-1 group cursor-pointer text-white/80 hover:text-white transition">
                <span className="text-xs font-semibold uppercase tracking-wide group-hover:underline decoration-green-400 underline-offset-4">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section - Video & PDF */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="relative z-10">
              <div className="inline-block px-4 py-1.5 rounded-full bg-green-50 text-green-600 font-bold text-xs uppercase tracking-wider mb-6">
                Como Funciona
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Simples, rápido e <br />
                <span className="text-green-600 relative">
                  intuitivo
                  <svg className="absolute w-full h-3 -bottom-1 left-0 text-green-200 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Descubra como o HoraMarcada Pro revoluciona a gestão do seu negócio ou a forma como você agenda serviços. Assista ao vídeo para ver a plataforma em ação.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="flex items-center justify-center gap-3 px-8 py-4 bg-slate-900 text-white rounded-full font-bold hover:bg-slate-800 transition shadow-lg hover:shadow-xl hover:-translate-y-1">
                  <PlayIcon className="h-6 w-6" />
                  <span>Ver Vídeo</span>
                </button>
                <a href="#" className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-slate-900 border-2 border-slate-100 rounded-full font-bold hover:border-slate-300 transition hover:bg-slate-50">
                  <DocumentTextIcon className="h-6 w-6 text-slate-500" />
                  <span>Baixar Apresentação (PDF)</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-slate-100 flex items-center gap-8">
                <div>
                  <p className="text-3xl font-bold text-slate-900">5k+</p>
                  <p className="text-sm text-slate-500">Profissionais</p>
                </div>
                <div className="w-px h-10 bg-slate-200"></div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">100k+</p>
                  <p className="text-sm text-slate-500">Agendamentos</p>
                </div>
              </div>
            </div>

            {/* Right Content - Video Placeholder */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl blur-2xl opacity-20 animate-pulse"></div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-100 bg-slate-900 aspect-video group cursor-pointer group">
                {/* Decorative Background for Video */}
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center opacity-60 group-hover:opacity-40 transition duration-500 transform group-hover:scale-105"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition duration-300 border border-white/40 shadow-2xl">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <PlayIcon className="h-8 w-8 text-green-600 ml-1" />
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="absolute bottom-0 left-0 w-full p-6 text-white">
                  <p className="text-sm font-medium text-green-400 mb-1">Demonstração</p>
                  <h3 className="text-xl font-bold">Tour Completo pela Plataforma</h3>
                  <p className="text-sm text-white/70 mt-1">2:30 min</p>
                </div>
              </div>

              {/* Floating element */}
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-xl border border-slate-50 hidden md:block animate-slow-bounce">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <SparklesIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-bold">Resultado</p>
                    <p className="text-sm font-bold text-slate-900">+30% Faturamento</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended Section (Real Images) */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Recomendados para você</h2>
            <p className="text-slate-500">Estabelecimentos no topo do ranking.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-12">
            {recommendedSpots.map((spot) => (
              <div key={spot.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group cursor-pointer">
                <div className="relative h-56 overflow-hidden">
                  <img src={spot.image} alt={spot.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 text-slate-900 shadow-sm">
                    <span className="text-yellow-500">★</span> {spot.rating} ({spot.reviews})
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold text-green-600 uppercase tracking-wide mb-1">{spot.type}</p>
                  <h3 className="text-xl font-bold text-slate-900">{spot.name}</h3>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col items-center gap-6 text-center">
            <Link to="/explorar" className="inline-flex items-center gap-2 text-green-600 font-bold hover:text-green-700 transition">
              Ver todos os estabelecimentos <span className="text-xl">→</span>
            </Link>

            <div className="w-full h-px bg-gray-200 max-w-xs mx-auto"></div>

            <Link to="/business" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-full font-bold text-sm hover:bg-slate-800 transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
              Listar sua empresa
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Minimal */}
      <footer className="bg-white py-8 border-t border-gray-100 text-center">
        <p className="text-slate-400 text-sm">© 2026 HoraMarcada Pro. Todos os direitos reservados.</p>
        <div className="flex justify-center gap-6 mt-4 text-sm text-slate-500">
          <Link to="/business" className="hover:text-green-600">Para Empresas</Link>
          <Link to="/terms" className="hover:text-green-600">Termos</Link>
          <Link to="/privacy" className="hover:text-green-600">Privacidade</Link>
        </div>
      </footer>

    </div>
  );
}